import {
  useOrganizationsServiceOrganizationsTeamsDestroy,
  useOrganizationsServiceOrganizationsTeamsList,
  useOrganizationsServiceOrganizationsTeamsListKey,
  useOrganizationsServiceOrganizationsTeamsPartialUpdate
} from '@organization/api/queries'
import { TeamList } from '@organization/api/requests'
import { messages } from '@organization/app-constants'
import { useTeamColumns } from '@organization/features/users/hooks'
import { getTableActions } from '@organization/features/users/utils/'
import {
  useInvalidateQueries,
  usePagination,
  usePaginationConfig,
  useQueryUpdate,
  useSessionContext
} from '@shared/hooks'
import { showApiErrors } from '@shared/utils'
import { App, MenuProps, Table } from 'antd'
import { InfoCircle } from 'iconsax-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { TeamForm } from '../Team'

import type { ExpandableConfig, SorterResult } from 'antd/es/table/interface'

type Props = {
  hasParent?: boolean
  parent?: number
  addTeamBtn?: (parent: number) => React.ReactNode
}

export const TeamsTable = ({
  hasParent = false,
  parent,
  addTeamBtn
}: Props) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [currentTeam, setCurrentTeam] = useState<TeamList | null>(null)
  const { message, modal } = App.useApp()
  const [ordering, setOrdering] = useState('')
  const { tab } = useParams()
  const { user } = useSessionContext()
  const pagination = usePaginationConfig()
  const localPagination = usePagination()
  const { params, updateQuery } = useQueryUpdate()

  const queryParams = {
    page: parent ? localPagination.current : pagination.current,
    perPage: parent ? localPagination.pageSize : pagination.pageSize,
    organization: Number(user?.organization?.id),
    excludeType: ['logical_node'],
    ...(params.search ? { search: params.search } : { hasParent, parent }),
    ...(!!ordering && { ordering })
  } as Parameters<typeof useOrganizationsServiceOrganizationsTeamsList>[0]

  const { data, isLoading } = useOrganizationsServiceOrganizationsTeamsList(
    queryParams,
    [queryParams],
    {
      enabled: !!user?.organization?.id && tab === 'teams'
    }
  )

  const { t } = useTranslation()
  const { invalidateQueries } = useInvalidateQueries()

  const onMutationSuccess = () => {
    invalidateQueries([useOrganizationsServiceOrganizationsTeamsListKey])
    setIsModalOpen(false)
  }

  const { mutate: removeTeam } =
    useOrganizationsServiceOrganizationsTeamsDestroy({
      onSuccess: () => {
        onMutationSuccess()
        message.success(messages.teamDeleted)
      },
      onError: (e) => showApiErrors(e)
    })

  const { mutate: editTeam } =
    useOrganizationsServiceOrganizationsTeamsPartialUpdate({
      onSuccess: () => onMutationSuccess(),
      onError: (e) => showApiErrors(e)
    })

  const confirmDelete = (id: number) => {
    modal.confirm({
      title: t('modal.delete_team'),
      okText: t('button.delete'),
      okType: 'danger',
      cancelText: t('button.cancel'),
      icon: <InfoCircle />,
      onOk: () => removeTeam({ id })
    })
  }

  const getActions = (team: TeamList): MenuProps['items'] => {
    const tableActions = getTableActions(t)

    return [
      tableActions.edit(() => {
        setCurrentTeam(team)
        setIsModalOpen(true)
      }),
      tableActions.delete(() => confirmDelete(Number(team?.id)))
    ]
  }

  const columns = useTeamColumns(getActions)

  const onTableChange = (
    _: unknown,
    __: unknown,
    sorter: SorterResult<TeamList> | SorterResult<TeamList>[]
  ) => {
    const isSingleSorter = (s: typeof sorter): s is SorterResult<TeamList> =>
      !Array.isArray(s)

    if (isSingleSorter(sorter) && sorter.column?.key && sorter.order) {
      const prefix = sorter.order === 'descend' ? '-' : ''
      setOrdering(`${prefix}${sorter.column.key}`)
    }
  }

  return (
    <>
      <Table
        showSorterTooltip={false}
        columns={columns}
        rowKey="id"
        dataSource={data?.results}
        loading={isLoading}
        onChange={onTableChange}
        pagination={{
          ...usePaginationConfig(data),
          onChange: (page: number, pageSize: number) =>
            updateQuery({
              ...params,
              page: page.toString(),
              pageSize: pageSize.toString()
            })
        }}
        {...(!params.search && {
          expandable: {
            rowExpandable: ({ has_children }) => has_children,
            expandedRowRender: ({ id }) => (
              <TeamsTable hasParent parent={id} addTeamBtn={addTeamBtn} />
            )
          } as ExpandableConfig<TeamList>
        })}
        {...(parent && {
          pagination: { ...localPagination, total: data?.count },
          footer: () => addTeamBtn?.(parent)
        })}
      />

      {isModalOpen && (
        <TeamForm
          key="rename-team"
          title={t('users.edit_team')}
          okText={t('button.edit')}
          team={currentTeam}
          isLoading={isLoading}
          onSubmit={(values) => {
            if (!currentTeam?.id) return
            editTeam({ id: currentTeam.id, requestBody: values })
          }}
          onCancel={() => setIsModalOpen(false)}
        />
      )}
    </>
  )
}
